import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider } from './context/AuthContext';
import { SettingsProvider } from './context/SettingsContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import PrivateRoute from './components/PrivateRoute';
import ScrollToTop from './components/ScrollToTop';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import Jobs from './pages/Jobs';
import JobDetails from './pages/JobDetails';
import MyApplications from './pages/MyApplications';
import SavedJobs from './pages/SavedJobs';
import Profile from './pages/Profile';
import Companies from './pages/Companies';
import CompanyDetails from './pages/CompanyDetails';
import Messages from './pages/Messages';
import Notifications from './pages/Notifications';
import PostJob from './pages/PostJob';
import CreateCompany from './pages/CreateCompany';
import Plans from './pages/Plans';
import Hire from './pages/Hire';
import ManageCompany from './pages/ManageCompany';

// Footer Pages
import AboutUs from './pages/AboutUs';
import ContactUs from './pages/ContactUs';
import Founder from './pages/Founder';
import FAQ from './pages/FAQ';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Careers from './pages/Careers';
import Pricing from './pages/Pricing';
import EmployerResources from './pages/EmployerResources';
import RecruitmentSolutions from './pages/RecruitmentSolutions';
import Blog from './pages/Blog';
import EmployerJobs from './pages/EmployerJobs';
import CompanyProfile from './pages/CompanyProfile';

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <SettingsProvider>
        <AuthProvider>
          <Router>
          <ScrollToTop />
          <Routes>
          {/* Routes without Footer */}
          <Route path="/create-company" element={
            <div className="flex flex-col h-screen overflow-hidden">
              <Navbar />
              <main className="flex-grow overflow-hidden relative flex flex-col">
                <PrivateRoute requiredRole="employer"><CreateCompany /></PrivateRoute>
              </main>
            </div>
          } />

          {/* Regular Routes - With Navbar/Footer */}
          <Route path="*" element={
            <div className="flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-grow">
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/jobs" element={<Jobs />} />
              <Route path="/jobs/:id" element={<JobDetails />} />
              <Route path="/companies" element={<Companies />} />
              <Route path="/companies/:id" element={<CompanyDetails />} />
              
              {/* Footer Pages */}
              <Route path="/about" element={<AboutUs />} />
              <Route path="/contact" element={<ContactUs />} />
              <Route path="/founder" element={<Founder />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/plans" element={<Plans />} />
              <Route
                path="/hire"
                element={
                  <PrivateRoute requiredRole="employer">
                    <Hire />
                  </PrivateRoute>
                }
              />
              <Route path="/blog" element={<Blog />} />
              <Route path="/employer-resources" element={<EmployerResources />} />
              <Route path="/recruitment-solutions" element={<RecruitmentSolutions />} />
              
              {/* Protected Routes */}
              <Route
                path="/messages"
                element={
                  <PrivateRoute>
                    <Messages />
                  </PrivateRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <PrivateRoute>
                    <Notifications />
                  </PrivateRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <PrivateRoute>
                    <Profile />
                  </PrivateRoute>
                }
              />
              <Route
                path="/applications"
                element={
                  <PrivateRoute>
                    <MyApplications />
                  </PrivateRoute>
                }
              />
              <Route
                path="/saved-jobs"
                element={
                  <PrivateRoute>
                    <SavedJobs />
                  </PrivateRoute>
                }
              />
              <Route
                path="/post-job"
                element={
                  <PrivateRoute requiredRole="employer">
                    <PostJob />
                  </PrivateRoute>
                }
              />
              {/* CreateCompany route has been moved to the outer Routes so it doesn't have a Footer */}
              <Route path="/manage-company" element={<PrivateRoute requiredRole="employer"><ManageCompany /></PrivateRoute>} />
              <Route path="/company-profile" element={<PrivateRoute requiredRole="employer"><CompanyProfile /></PrivateRoute>} />

              <Route
                path="/my-jobs"
                element={
                  <PrivateRoute requiredRole="employer">
                    <EmployerJobs />
                  </PrivateRoute>
                }
              />
              
              {/* 404 */}
              <Route
                path="*"
                element={
                  <div className="container mx-auto px-4 py-16 text-center">
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">404 - Page Not Found</h1>
                    <p className="text-gray-600 mb-8">The page you're looking for doesn't exist.</p>
                    <a href="/" className="btn-primary">Go Home</a>
                  </div>
                }
              />
                </Routes>
              </main>
              <Footer />
            </div>
          } />
        </Routes>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </Router>
    </AuthProvider>
    </SettingsProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
